import { useFormik } from 'formik';
import React from 'react';

import * as ManifestUI from '@project44/manifest-ds';
import { User } from '@project44/ui-utils';
import {
  DtoBase,
  TenantModels,
  UserModels,
} from '@project44-models/iam-gateway';

import { UIFormClient } from '../../../../../external/UIFormClient/domain/abstract/UIFormClient';
import { IFormClient } from '../../../../../external/UIFormClient/domain/interface';
import { IAxiosApiState } from '../../../../../shared/core/domain/types';
import { TAnyOfPermissionsHOC } from '../../../../../shared/core/infrastructrue/types';
import { IUserMoveForm } from '../../../domain/interfaces';
import { UserI18NPort } from '../../ports/User.i18n.port';

export type TUserMoveFormView = {
  UserMoveFormCreator: UIFormClient<IUserMoveForm>;
  userPrincipal: User;
  userToMove: DtoBase<UserModels.UserViewModel>;
  moveUserAxiosDataState: IAxiosApiState;
  moveUserAxiosHandler: (payload: {
    userDto: DtoBase<UserModels.UserViewModel>;
    targetTenantUuid: string;
  }) => Promise<void>;
  TenantMover: React.FC<
    TAnyOfPermissionsHOC<{
      user: User;
      moveUserAxiosDataState: IAxiosApiState;
      tenantSelectorHandler: (options: {
        tenantDto: DtoBase<TenantModels.TenantViewModel>;
      }) => Promise<void>;
    }>
  >;
  isApiRequestInProgress?: boolean;
};

export const UserMoveFormView: React.FC<TUserMoveFormView> = ({
  UserMoveFormCreator,
  isApiRequestInProgress,
  TenantMover,
  userPrincipal,
  userToMove,
  moveUserAxiosDataState,
  moveUserAxiosHandler,
}) => {
  const { spaceMedium } = ManifestUI.designTokens;

  const formClient = useFormik<IUserMoveForm>(
    UserMoveFormCreator.configureFormClient(async ({ targetTenantUuid }) => {
      return moveUserAxiosHandler({ targetTenantUuid, userDto: userToMove });
    }),
  ) as unknown as IFormClient;

  React.useEffect(() => {
    if (formClient.values.existingTenantUuid === '') {
      formClient.setFieldValue(
        'existingTenantUuid',
        userToMove.source.getTenantUuid(),
      );
    }

    if (formClient.values.username === '') {
      formClient.setFieldValue('username', userToMove.source.getUsername());
    }
  }, [formClient, userToMove.source]);

  return (
    <>
      <ManifestUI.Grid
        as={'form'}
        aria-label={`user-move-form`}
        gap="small"
        onSubmit={(event) => {
          event.preventDefault();
          formClient.handleSubmit();
        }}
        id="user-move-form"
        children={
          <>
            <ManifestUI.GridItem
              column={12}
              css={{ marginTop: spaceMedium }}
              children={
                <UserMoveFormCreator.renderHTMLFormProperty
                  withFormControl
                  label={UserI18NPort.scopedTranslator(
                    'userMoveForm.confirmUsername',
                    { userToMove: userToMove.source.getUsername() },
                  )}
                  propertyName={'confirmUsername'}
                  formClient={formClient}
                  isLoading={isApiRequestInProgress}
                />
              }
            />
            <ManifestUI.GridItem
              column={12}
              css={{ marginTop: spaceMedium }}
              children={
                <UserMoveFormCreator.renderHTMLFormProperty
                  label={UserI18NPort.scopedTranslator(
                    'userMoveForm.targetTenantName',
                    {
                      userToMove: `${userToMove.source.getGivenName()} ${userToMove.source.getFamilyName()}`,
                    },
                  )}
                  propertyName={'targetTenantName'}
                  formClient={formClient}
                  isLoading={isApiRequestInProgress}
                />
              }
            />
          </>
        }
      />
      <ManifestUI.Box
        css={{ width: '100%' }}
        children={
          <ManifestUI.GridItem
            column={12}
            children={
              <TenantMover
                moveUserAxiosDataState={{
                  ...moveUserAxiosDataState,
                  isLoading: !!isApiRequestInProgress,
                }}
                tenantSelectorHandler={async ({ tenantDto }) => {
                  const tenant = tenantDto.toSerializedModel();

                  formClient.setFieldValue(
                    'targetTenantUuid',
                    tenant.tenantUuid,
                  );
                  formClient.setFieldValue('targetTenantName', tenant.name);
                }}
                user={userPrincipal}
              />
            }
          />
        }
      />
    </>
  );
};
